import React, { useState, useEffect } from "react";
import { Contact } from "./ApplicationWizard";
import "./ContactForm.css";

interface ContactFormProps {
  contact?: Contact | null;
  onSave: (contact: Contact) => void;
  onCancel: () => void;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  contact = null,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState<Partial<Contact>>({
    name: contact?.name || null,
    title: contact?.title || null,
    email: contact?.email || null,
    phone: contact?.phone || null,
    linkedinUrl: contact?.linkedinUrl || null,
    contactType: contact?.contactType || "other",
    notes: contact?.notes || null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const contactId = contact?.id;
  const contactName = contact?.name;
  const contactTitle = contact?.title;
  const contactEmail = contact?.email;
  const contactPhone = contact?.phone;
  const contactLinkedin = contact?.linkedinUrl;
  const contactType = contact?.contactType;
  const contactNotes = contact?.notes;

  useEffect(() => {
    if (!contact) return;
    setFormData({
      name: contactName || null,
      title: contactTitle || null,
      email: contactEmail || null,
      phone: contactPhone || null,
      linkedinUrl: contactLinkedin || null,
      contactType: contactType || "other",
      notes: contactNotes || null,
    });
  }, [
    contactId,
    contactName,
    contactTitle,
    contactEmail,
    contactPhone,
    contactLinkedin,
    contactType,
    contactNotes,
  ]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // At least name or email required
    if (!formData.name && !formData.email) {
      newErrors.name = "Name or email is required";
      newErrors.email = "Name or email is required";
    }

    // Email format validation if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const contactToSave: Contact = {
      id: contact?.id,
      name: formData.name || null,
      title: formData.title || null,
      email: formData.email || null,
      phone: formData.phone || null,
      linkedinUrl: formData.linkedinUrl || null,
      contactType: formData.contactType || "other",
      notes: formData.notes || null,
      extracted: contact?.extracted || false,
    };

    onSave(contactToSave);
  };

  const contactTypes: Array<{ value: Contact["contactType"]; label: string }> =
    [
      { value: "job_poster", label: "Job Poster" },
      { value: "recruiter", label: "Recruiter" },
      { value: "hiring_manager", label: "Hiring Manager" },
      { value: "interviewer", label: "Interviewer" },
      { value: "other", label: "Other" },
    ];

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="contact-form-header">
        <h3>{contact ? "Edit Contact" : "Add Contact"}</h3>
      </div>

      <div className="contact-form-body">
        <div className="contact-form-row">
          <div className="contact-form-field">
            <label htmlFor="contact-name">
              Name <span className="required-indicator">*</span>
            </label>
            <input
              id="contact-name"
              type="text"
              value={formData.name || ""}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value || null })
              }
              placeholder="Full name"
              className={errors.name ? "error" : ""}
            />
            {errors.name && (
              <span className="error-message">{errors.name}</span>
            )}
          </div>

          <div className="contact-form-field">
            <label htmlFor="contact-title">Title</label>
            <input
              id="contact-title"
              type="text"
              value={formData.title || ""}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value || null })
              }
              placeholder="e.g., Senior Talent Acquisition Partner"
            />
          </div>
        </div>

        <div className="contact-form-row">
          <div className="contact-form-field">
            <label htmlFor="contact-email">
              Email <span className="required-indicator">*</span>
            </label>
            <input
              id="contact-email"
              type="email"
              value={formData.email || ""}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value || null })
              }
              placeholder="email@example.com"
              className={errors.email ? "error" : ""}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="contact-form-field">
            <label htmlFor="contact-phone">Phone</label>
            <input
              id="contact-phone"
              type="tel"
              value={formData.phone || ""}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value || null })
              }
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        <div className="contact-form-row">
          <div className="contact-form-field">
            <label htmlFor="contact-linkedin">LinkedIn URL</label>
            <input
              id="contact-linkedin"
              type="url"
              value={formData.linkedinUrl || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  linkedinUrl: e.target.value || null,
                })
              }
              placeholder="https://linkedin.com/in/username"
            />
          </div>

          <div className="contact-form-field">
            <label htmlFor="contact-type">Contact Type</label>
            <select
              id="contact-type"
              value={formData.contactType || "other"}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  contactType: e.target.value as Contact["contactType"],
                })
              }
            >
              {contactTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="contact-form-field full-width">
          <label htmlFor="contact-notes">Notes</label>
          <textarea
            id="contact-notes"
            value={formData.notes || ""}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value || null })
            }
            placeholder="Additional details, meeting notes, etc."
            rows={3}
          />
        </div>
      </div>

      <div className="contact-form-footer">
        <button type="button" className="cancel-button" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="save-button">
          {contact ? "Update" : "Add"} Contact
        </button>
      </div>
    </form>
  );
};
